import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  Check,
  ChevronRight,
  Lock,
  Play,
  ShieldCheck,
  Sparkles,
  Volume2,
  VolumeX,
  Zap,
} from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Progress } from '@/components/ui/progress';
import { Separator } from '@/components/ui/separator';
import { useGround } from '@/hooks/useGround';
import { useGroundPublish } from '@/hooks/useGroundPublish';
import { useGroundVoice } from '@/hooks/useGroundVoice';
import { useToast } from '@/hooks/useToast';
import {
  PROTOCOLS,
  TOTAL_ROADMAP_ITEMS,
  type Protocol,
  type ProtocolSession,
  type RoadmapTier,
} from '@/lib/ground/protocols';
import { cn } from '@/lib/utils';
import { formatClock } from '@/lib/ground/utils';
import { PurchaseDialog } from './PurchaseDialog';
import { SectionTimer } from './SectionTimer';

interface ProtocolsProps {
  focusProtocolId?: string | null;
}

export function Protocols({ focusProtocolId }: ProtocolsProps) {
  const { foundationsComplete, ownsProtocol } = useGround();
  const [selectedId, setSelectedId] = useState<string | null>(() =>
    focusProtocolId && ownsProtocol(focusProtocolId) ? focusProtocolId : null,
  );
  const [buying, setBuying] = useState<Protocol | null>(() => {
    if (focusProtocolId && !ownsProtocol(focusProtocolId)) {
      return PROTOCOLS.find((protocol) => protocol.id === focusProtocolId) ?? null;
    }
    return null;
  });

  const selected = selectedId ? PROTOCOLS.find((protocol) => protocol.id === selectedId) ?? null : null;

  if (!foundationsComplete) {
    return <LockedProtocols />;
  }

  if (selected && ownsProtocol(selected.id)) {
    return <ProtocolDetail protocol={selected} onBack={() => setSelectedId(null)} />;
  }

  return (
    <div className="space-y-6">
      <div className="max-w-2xl">
        <h2 className="font-display text-3xl font-semibold tracking-tight sm:text-4xl">
          Protocols
        </h2>
        <p className="mt-3 text-muted-foreground">
          Deeper, paid programmes that build on the free Foundations. Buy individually or take the
          bundle. Purchases are simulated while demo mode is on.
        </p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {PROTOCOLS.map((protocol) => {
          const owned = ownsProtocol(protocol.id);
          const isBundle = protocol.kind === 'bundle';
          return (
            <Card
              key={protocol.id}
              className={cn(
                'relative flex flex-col overflow-hidden',
                isBundle && 'border-primary/40',
              )}
            >
              <div className={cn('bg-gradient-to-br px-5 pt-5 pb-4', protocol.hue)}>
                <div className="flex items-start justify-between gap-3">
                  <span className="text-3xl" aria-hidden>
                    {protocol.emoji}
                  </span>
                  {owned ? (
                    <Badge variant="secondary" className="gap-1 font-normal">
                      <Check className="size-3" />
                      Owned
                    </Badge>
                  ) : isBundle ? (
                    <Badge className="font-normal">Best value</Badge>
                  ) : null}
                </div>
                <h3 className="mt-3 font-display text-xl font-semibold">{protocol.title}</h3>
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <Badge variant="secondary" className="font-normal">
                    {protocol.sessionCount} sessions
                  </Badge>
                  <Badge variant="outline" className="font-normal tabular-nums">
                    {protocol.priceSats.toLocaleString()} sats
                  </Badge>
                </div>
              </div>

              <CardContent className="flex flex-1 flex-col gap-4 py-5">
                <p className="flex-1 text-sm leading-relaxed text-muted-foreground">
                  {protocol.description}
                </p>
                {owned ? (
                  <Button
                    className="w-full gap-1.5"
                    onClick={() => setSelectedId(protocol.id)}
                  >
                    Open
                    <ChevronRight className="size-4" />
                  </Button>
                ) : (
                  <Button
                    variant="outline"
                    className="w-full gap-1.5"
                    onClick={() => setBuying(protocol)}
                  >
                    <Zap className="size-4" />
                    Buy for {protocol.priceSats.toLocaleString()} sats
                  </Button>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      <PurchaseDialog protocol={buying} open={buying !== null} onOpenChange={(open) => !open && setBuying(null)} />
    </div>
  );
}

function LockedProtocols() {
  return (
    <div className="mx-auto max-w-xl">
      <Card className="overflow-hidden border-dashed">
        <CardContent className="flex flex-col items-center px-8 py-16 text-center">
          <div className="flex size-16 items-center justify-center rounded-2xl bg-secondary text-3xl">
            🔒
          </div>
          <h2 className="mt-5 font-display text-2xl font-semibold">Complete Foundations first</h2>
          <p className="mt-3 max-w-sm text-muted-foreground">
            The Protocols unlock once you finish all five days of the free Foundations protocol.
            Head back to the Foundations tab to keep going.
          </p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            {PROTOCOLS.map((protocol) => (
              <Badge key={protocol.id} variant="secondary" className="gap-1.5 font-normal">
                <Lock className="size-3" />
                {protocol.emoji} {protocol.title}
              </Badge>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function ProtocolDetail({ protocol, onBack }: { protocol: Protocol; onBack: () => void }) {
  if (protocol.kind === 'roadmap') {
    return <RoadmapDetail protocol={protocol} onBack={onBack} />;
  }

  return <SessionList protocol={protocol} onBack={onBack} />;
}

function SessionList({ protocol, onBack }: { protocol: Protocol; onBack: () => void }) {
  const [active, setActive] = useState<ProtocolSession | null>(null);
  const sessions = protocol.sessions ?? [];

  if (active) {
    return (
      <SessionPlayer
        protocol={protocol}
        session={active}
        onBack={() => setActive(null)}
      />
    );
  }

  return (
    <div className="mx-auto max-w-3xl space-y-5">
      <Button variant="ghost" size="sm" onClick={onBack} className="-ml-2">
        <ArrowLeft className="size-4" />
        All protocols
      </Button>

      <div className="flex items-center gap-4">
        <div className="flex size-14 items-center justify-center rounded-2xl bg-secondary text-3xl" aria-hidden>
          {protocol.emoji}
        </div>
        <div>
          <h2 className="font-display text-3xl font-semibold tracking-tight">{protocol.title}</h2>
          <p className="text-muted-foreground">{protocol.sessionCount} sessions</p>
        </div>
      </div>

      <p className="text-[15px] text-muted-foreground">{protocol.description}</p>

      <div className="space-y-3">
        {sessions.map((session, index) => (
          <button
            key={session.id}
            type="button"
            onClick={() => setActive(session)}
            className={cn(
              'flex w-full items-center gap-4 rounded-xl border bg-card p-4 text-left shadow-sm transition-all',
              'hover:-translate-y-0.5 hover:border-primary/50 hover:shadow-md',
              'focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 focus-visible:outline-none',
            )}
          >
            <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-secondary font-display text-sm tabular-nums">
              {index + 1}
            </div>
            <div className="min-w-0 flex-1">
              <div className="font-medium">{session.title}</div>
              <div className="text-xs text-muted-foreground">{formatClock(session.durationSec)} guided</div>
            </div>
            <Play className="size-4 text-muted-foreground" />
          </button>
        ))}
      </div>
    </div>
  );
}

function SessionPlayer({
  protocol,
  session,
  onBack,
}: {
  protocol: Protocol;
  session: ProtocolSession;
  onBack: () => void;
}) {
  const [finished, setFinished] = useState(false);
  const [completed, setCompleted] = useState(false);
  const voice = useGroundVoice();
  const { addSession, addSatsEarned, settings } = useGround();
  const { mutate: publish, isPending } = useGroundPublish();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const handleComplete = () => {
    const content = `Completed a ${protocol.title} session: ${session.title}`;
    publish(
      {
        kind: 1,
        content,
        tags: [
          ['t', 'ground'],
          ['t', 'protocols'],
          ['t', protocol.id],
          ['alt', content],
        ],
      },
      {
        onSuccess: ({ published }) => {
          setCompleted(true);
          addSession();
          if (settings.demoMode) addSatsEarned(21);
          queryClient.invalidateQueries({ queryKey: ['ground', 'feed'] });
          toast({
            title: 'Session complete',
            description: published
              ? 'Your session was shared to Nostr.'
              : 'Saved locally — relays were unreachable just now.',
          });
        },
        onError: () => {
          toast({
            title: 'Could not publish',
            description: 'Something went wrong signing your completion.',
            variant: 'destructive',
          });
        },
      },
    );
  };

  return (
    <div className="mx-auto max-w-2xl space-y-5">
      <Button variant="ghost" size="sm" onClick={onBack} className="-ml-2">
        <ArrowLeft className="size-4" />
        {protocol.title}
      </Button>

      <Card className="overflow-hidden">
        <div className={cn('bg-gradient-to-br px-6 pt-6 pb-4', protocol.hue)}>
          <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
            <span aria-hidden>{protocol.emoji}</span>
            {protocol.title}
          </div>
          <h2 className="mt-2 font-display text-2xl font-semibold">{session.title}</h2>
          <div className="mt-3">
            <SectionTimer seconds={session.durationSec} done={finished} onDone={() => setFinished(true)} />
          </div>
        </div>

        <Separator />

        <CardContent className="py-6">
          <p className="whitespace-pre-wrap text-lg leading-relaxed text-foreground/90">
            {session.script.replace(/\{pause\}/g, '\n\n')}
          </p>
        </CardContent>
      </Card>

      <div className="flex flex-wrap items-center gap-3">
        <Button
          variant="outline"
          className="gap-2"
          onClick={() => (voice.status === 'playing' ? voice.stop() : voice.play(session.script))}
        >
          {voice.status === 'playing' ? <VolumeX className="size-4" /> : <Volume2 className="size-4" />}
          {voice.status === 'playing' ? 'Stop narration' : 'Listen'}
        </Button>

        {!finished && (
          <Button variant="ghost" className="gap-2 text-muted-foreground" onClick={() => setFinished(true)}>
            <Check className="size-4" />
            Mark done
          </Button>
        )}

        <div className="ml-auto">
          <Button
            variant={completed ? 'outline' : 'default'}
            className="gap-2"
            onClick={handleComplete}
            disabled={isPending || completed}
          >
            {completed ? <Check className="size-4" /> : <Sparkles className="size-4" />}
            {completed ? 'Shared to Nostr' : 'Complete'}
          </Button>
        </div>
      </div>

      {voice.error && (
        <p className="rounded-lg border border-dashed px-4 py-3 text-sm text-muted-foreground">
          {voice.error}
        </p>
      )}
    </div>
  );
}

function RoadmapDetail({ protocol, onBack }: { protocol: Protocol; onBack: () => void }) {
  const { checkedItems, toggleItem, resetRoadmap, settings, addSatsEarned } = useGround();
  const { mutate: publish } = useGroundPublish();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const tiers = protocol.tiers ?? [];
  const completed = checkedItems.length;
  const percent = Math.round((completed / TOTAL_ROADMAP_ITEMS) * 100);

  const handleToggle = (id: string, text: string, tierId: string) => {
    const nowChecked = toggleItem(id);
    if (!nowChecked) return;

    const content = `Checked off: ${text}`;
    publish(
      {
        kind: 1,
        content,
        tags: [
          ['t', 'ground'],
          ['t', 'digital-sovereignty'],
          ['t', tierId],
          ['alt', content],
        ],
      },
      {
        onSuccess: ({ published }) => {
          if (settings.demoMode) addSatsEarned(11);
          queryClient.invalidateQueries({ queryKey: ['ground', 'feed'] });
          toast({
            title: 'Step taken',
            description: published
              ? 'Your progress was shared to Nostr.'
              : 'Saved locally — relays were unreachable just now.',
          });
        },
      },
    );
  };

  return (
    <div className="mx-auto max-w-3xl space-y-5">
      <Button variant="ghost" size="sm" onClick={onBack} className="-ml-2">
        <ArrowLeft className="size-4" />
        All protocols
      </Button>

      <div className="flex items-center gap-4">
        <div className="flex size-14 items-center justify-center rounded-2xl bg-secondary text-3xl" aria-hidden>
          {protocol.emoji}
        </div>
        <div>
          <h2 className="font-display text-3xl font-semibold tracking-tight">{protocol.title}</h2>
          <p className="text-muted-foreground">{protocol.sessionCount} steps across 5 tiers</p>
        </div>
      </div>

      <Card>
        <CardContent className="space-y-3 py-6">
          <div className="flex flex-wrap items-end justify-between gap-3">
            <div>
              <div className="font-display text-3xl font-semibold tabular-nums">{percent}%</div>
              <p className="text-sm text-muted-foreground">
                {completed} of {TOTAL_ROADMAP_ITEMS} steps complete
              </p>
            </div>
            {completed > 0 && (
              <Button
                variant="ghost"
                size="sm"
                className="text-muted-foreground"
                onClick={() => {
                  resetRoadmap();
                  toast({ title: 'Roadmap reset', description: 'All steps unchecked locally.' });
                }}
              >
                Reset
              </Button>
            )}
          </div>
          <Progress value={percent} className="h-2.5" />
        </CardContent>
      </Card>

      <div className="space-y-4">
        {tiers.map((tier, tierIndex) => (
          <TierCard
            key={tier.id}
            tier={tier}
            tierIndex={tierIndex}
            checkedItems={checkedItems}
            onToggle={handleToggle}
          />
        ))}
      </div>
    </div>
  );
}

function TierCard({
  tier,
  tierIndex,
  checkedItems,
  onToggle,
}: {
  tier: RoadmapTier;
  tierIndex: number;
  checkedItems: string[];
  onToggle: (id: string, text: string, tierId: string) => void;
}) {
  const tierDone = tier.items.filter((item) => checkedItems.includes(item.id)).length;
  const tierComplete = tierDone === tier.items.length;

  return (
    <Card className={cn('transition-colors', tierComplete && 'border-primary/40')}>
      <CardHeader className="border-b">
        <div className="flex items-start gap-4">
          <div
            className={cn(
              'flex size-11 shrink-0 items-center justify-center rounded-lg bg-secondary text-xl',
              tierComplete && 'bg-primary/15',
            )}
            aria-hidden
          >
            {tier.emoji}
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <CardTitle className="font-display text-lg">
                <span className="text-muted-foreground">{tierIndex + 1}. </span>
                {tier.title}
              </CardTitle>
              {tierComplete && <ShieldCheck className="size-4 text-primary" />}
            </div>
            <p className="mt-1 text-sm text-muted-foreground">{tier.description}</p>
          </div>
          <Badge variant="outline" className="font-normal tabular-nums">
            {tierDone}/{tier.items.length}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        <ul className="divide-y divide-border">
          {tier.items.map((item) => {
            const isChecked = checkedItems.includes(item.id);
            return (
              <li key={item.id}>
                <label
                  className={cn(
                    'flex cursor-pointer items-center gap-3 py-3.5 transition-colors hover:text-foreground',
                    isChecked && 'text-muted-foreground',
                  )}
                >
                  <Checkbox
                    checked={isChecked}
                    onCheckedChange={() => onToggle(item.id, item.text, tier.id)}
                    aria-label={item.text}
                  />
                  <span className={cn('text-[15px]', isChecked && 'line-through')}>{item.text}</span>
                </label>
              </li>
            );
          })}
        </ul>
      </CardContent>
    </Card>
  );
}
